import React, { useEffect, useState } from 'react';
import { Box, Card, Heading, SimpleGrid, Spinner, Text, Flex, Icon, Table, Thead, Tbody, Tr, Th, Td, Avatar, Badge, TableContainer, Button } from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Axios from '../../utils/httpClient';
import { FaUsers, FaTrophy, FaChartLine, FaStar, FaMedal } from 'react-icons/fa';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316', '#06B6D4', '#84CC16', '#EAB308'];

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const StatCard = ({ title, value, subtext, icon, colorScheme }) => (
    <MotionCard
        whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
        transition={{ duration: 0.2 }}
        p={5}
        borderRadius="xl"
        bg="rgba(255, 255, 255, 0.9)"
        backdropFilter="blur(10px)"
        border="1px solid"
        borderColor="whiteAlpha.300"
        boxShadow="xl"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
    >
        <Box>
            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                {title}
            </Text>
            <Heading size="lg" mt={2} color="gray.800">
                {value}
            </Heading>
            {subtext && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                    {subtext}
                </Text>
            )}
        </Box>
        <Flex
            w={12}
            h={12}
            align="center"
            justify="center"
            borderRadius="full"
            bg={`${colorScheme}.100`}
            color={`${colorScheme}.500`}
            boxShadow="sm"
        >
            <Icon as={icon} boxSize={5} />
        </Flex>
    </MotionCard>
);

const ProRectorDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data1, setData1] = useState([]);
    const [data2, setData2] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [topTeachers, setTopTeachers] = useState([]);
    const [rangeStats, setRangeStats] = useState({ '100-70': 0, '70-50': 0, '50-30': 0, '30-0': 0 });
    const [totalStats, setTotalStats] = useState({ 
        total: 0, 
        teacherCount: 0, 
        avg: 0,
        topKafedra: { name: '-', avg: 0 } 
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await Axios().get('api/v1/account/user-results/');
            const users = res?.data || [];
            processData(users);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const processData = (users) => {
        if (!users || users.length === 0) {
            setData1([]);
            setData2([]);
            setPieData([]);
            setTopTeachers([]);
            return;
        }

        const departments = {};
        let grandTotal = 0;
        let totalTeachers = 0;

        users.forEach(user => {
            const deptName = user.kafedra_name || "Boshqa";

            if (deptName === "Boshqa") return;

            if (!departments[deptName]) {
                departments[deptName] = { points: 0, teachers: 0, name: deptName };
            }
            const userBall = parseFloat(user.ball) || 0;
            departments[deptName].points += userBall;
            departments[deptName].teachers += 1;
            grandTotal += userBall;
            totalTeachers += 1;
        });

        const processedData = Object.values(departments).map(d => ({
            ...d,
            ratio: d.teachers > 0 ? (d.points / d.teachers).toFixed(2) : 0
        }));

        processedData.sort((a, b) => b.ratio - a.ratio);
        
        let topKafedra = { name: '-', avg: 0 };
        if (processedData.length > 0) {
            topKafedra = { name: processedData[0].name, avg: processedData[0].ratio };
        }

        const d1 = processedData.map((d, index) => ({
            name: d.name,
            value: parseFloat(d.ratio),
            rank: index + 1
        }));

        const d2 = [...processedData].sort((a, b) => a.name.localeCompare(b.name)).map(d => ({
            name: d.name,
            points: parseFloat(d.points.toFixed(2)),
            teachers: d.teachers
        }));

        const pData = processedData.map(d => ({
            name: d.name,
            value: parseFloat(d.points.toFixed(2))
        })).filter(d => d.value > 0);

        // Process Top Teachers
        const sortedUsers = [...users]
            .filter(u => u.kafedra_name !== "Boshqa" && parseFloat(u.ball) > 0)
            .sort((a, b) => parseFloat(b.ball) - parseFloat(a.ball))
            .slice(0, 10);

        setTopTeachers(sortedUsers);

        // Process Range Stats
        const ranges = {
            '100-70': users.filter(u => u.kafedra_name !== "Boshqa" && parseFloat(u.ball) >= 70 && parseFloat(u.ball) <= 100).length,
            '70-50': users.filter(u => u.kafedra_name !== "Boshqa" && parseFloat(u.ball) >= 50 && parseFloat(u.ball) < 70).length,
            '50-30': users.filter(u => u.kafedra_name !== "Boshqa" && parseFloat(u.ball) >= 30 && parseFloat(u.ball) < 50).length,
            '30-0': users.filter(u => u.kafedra_name !== "Boshqa" && parseFloat(u.ball) >= 0 && parseFloat(u.ball) < 30).length
        };
        setRangeStats(ranges);

        setTotalStats({ 
            total: grandTotal.toFixed(2), 
            teacherCount: totalTeachers,
            avg: totalTeachers > 0 ? (grandTotal / totalTeachers).toFixed(2) : 0,
            topKafedra
        });
        setPieData(pData);
        setData1(d1);
        setData2(d2);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bg="gray.50">
                <Spinner size="xl" color="indigo.500" thickness="4px" />
            </Box>
        );
    }

    if (data1.length === 0) {
        return (
            <Box p={5} textAlign="center" minH="100vh" bg="gray.50" pt={20}>
                <Heading mb={5} color="gray.700">KPI Dashboard</Heading>
                <Card p={10} maxW="lg" mx="auto" borderRadius="2xl" boxShadow="xl">
                    <Heading size="md" color="gray.500">Ma'lumotlar mavjud emas</Heading>
                    <Text mt={4} color="gray.400">Tizimda tasdiqlangan KPI arizalari topilmadi.</Text>
                </Card>
            </Box>
        );
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius * 1.25;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="#4B5563" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight="600">
                {`${name} (${(percent * 100).toFixed(0)}%)`}
            </text>
        );
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    return (
        <Box p={6} bgGradient="linear(to-br, gray.50, blue.50)" minH="100vh">
            <MotionBox initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Heading mb={8} color="gray.800" fontWeight="bold" letterSpacing="tight">
                    Umumiy KPI Dashboard
                </Heading>
            </MotionBox>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
                <StatCard 
                    title="Jami O'qituvchilar" 
                    value={totalStats.teacherCount} 
                    icon={FaUsers} 
                    colorScheme="blue" 
                />
                <StatCard 
                    title="Jami To'plangan Ball" 
                    value={totalStats.total} 
                    icon={FaTrophy} 
                    colorScheme="green" 
                />
                <StatCard 
                    title="O'rtacha Ball" 
                    value={totalStats.avg} 
                    subtext="Bitta o'qituvchiga nisbatan"
                    icon={FaChartLine} 
                    colorScheme="purple" 
                />
                <StatCard 
                    title="Eng Ilg'or Kafedra" 
                    value={totalStats.topKafedra.name} 
                    subtext={`O'rtacha: ${totalStats.topKafedra.avg} ball`}
                    icon={FaStar} 
                    colorScheme="orange" 
                />
            </SimpleGrid>

            <MotionBox variants={containerVariants} initial="hidden" animate="visible">
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8} mb={8}>
                    {/* Top Teachers List */}
                    <MotionBox variants={itemVariants} gridColumn={{ base: "1", xl: "1 / span 2" }}>
                        <Card p={6} borderRadius="2xl" boxShadow="xl" bg="white" border="1px solid" borderColor="gray.100">
                            <Flex align="center" justify="space-between" mb={6}>
                                <Flex align="center">
                                    <Icon as={FaMedal} color="yellow.400" boxSize={6} mr={3} />
                                    <Heading size="md" color="gray.700">Top 10 Ilg'or O'qituvchilar</Heading>
                                </Flex>
                                <Button 
                                    size="sm" 
                                    colorScheme="blue" 
                                    variant="outline" 
                                    onClick={() => navigate('/dashboard/drill-down')}
                                >
                                    Barchasini ko'rish
                                </Button>
                            </Flex>
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th w="50px">O'rin</Th>
                                            <Th>F.I.Sh</Th>
                                            <Th>Kafedra</Th>
                                            <Th isNumeric>Jami Ball</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {topTeachers.map((t, i) => (
                                            <Tr key={t.id} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                                                <Td>
                                                    <Badge 
                                                        colorScheme={i === 0 ? "yellow" : i === 1 ? "gray" : i === 2 ? "orange" : "blue"} 
                                                        fontSize="xs" 
                                                        px={2} 
                                                        py={1} 
                                                        borderRadius="md"
                                                    >
                                                        #{i + 1}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Flex align="center">
                                                        <Avatar size="sm" name={`${t.last_name} ${t.first_name}`} src={t.avatar} mr={3} />
                                                        <Box>
                                                            <Text fontWeight="bold" color="gray.700" fontSize="sm">{t.last_name} {t.first_name} {t.middle_name}</Text>
                                                            <Text fontSize="xs" color="gray.500">{t.position}</Text>
                                                        </Box>
                                                    </Flex>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" color="gray.600" noOfLines={1}>{t.kafedra_name}</Text>
                                                </Td>
                                                <Td isNumeric>
                                                    <Text fontWeight="bold" color="indigo.600" fontSize="sm">{parseFloat(t.ball).toFixed(2)}</Text>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </MotionBox>

                    <MotionBox variants={itemVariants}>
                        <Card p={6} borderRadius="2xl" boxShadow="xl" bg="white" border="1px solid" borderColor="gray.100" h="100%">
                            <Heading size="sm" mb={6} color="gray.600" textTransform="uppercase" letterSpacing="wider">
                                Ballar Kesimidagi Taqsimot
                            </Heading>
                            <Box height="350px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: '100-70 ball', value: rangeStats['100-70'], color: '#4ADE80', range: '100-70' },
                                                { name: '70-50 ball', value: rangeStats['70-50'], color: '#60A5FA', range: '70-50' },
                                                { name: '50-30 ball', value: rangeStats['50-30'], color: '#FCD34D', range: '50-30' },
                                                { name: '30-0 ball', value: rangeStats['30-0'], color: '#FCA5A5', range: '30-0' }
                                            ].filter(d => d.value > 0)}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={renderCustomizedLabel}
                                            outerRadius={110}
                                            dataKey="value"
                                            isAnimationActive={true}
                                            paddingAngle={2}
                                            onClick={(data) => {
                                                if (data && data.payload && data.payload.range) {
                                                    navigate(`/teachers-with-points-range/${data.payload.range}`);
                                                }
                                            }}
                                            style={{ cursor: 'pointer', outline: 'none' }}
                                        >
                                            {
                                                [
                                                    { name: '100-70 ball', value: rangeStats['100-70'], color: '#4ADE80' },
                                                    { name: '70-50 ball', value: rangeStats['70-50'], color: '#60A5FA' },
                                                    { name: '50-30 ball', value: rangeStats['50-30'], color: '#FCD34D' },
                                                    { name: '30-0 ball', value: rangeStats['30-0'], color: '#FCA5A5' }
                                                ].filter(d => d.value > 0).map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))
                                            }
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => [`${value} nafar`, 'O\'qituvchilar soni']}
                                            contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    <MotionBox variants={itemVariants}>
                        <Card p={6} borderRadius="2xl" boxShadow="xl" bg="white" border="1px solid" borderColor="gray.100" h="100%">
                            <Heading size="sm" mb={6} color="gray.600" textTransform="uppercase" letterSpacing="wider">
                                Jamlangan Ballar Tahlili (Kafedralar)
                            </Heading>
                            <Box height="350px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={renderCustomizedLabel}
                                            outerRadius={110}
                                            fill="#8884d8"
                                            dataKey="value"
                                            isAnimationActive={true}
                                            paddingAngle={2}
                                            onClick={(data) => {
                                                if (data && data.name) {
                                                    navigate(`/dashboard/drill-down?kafedra=${encodeURIComponent(data.name)}`);
                                                }
                                            }}
                                            style={{ cursor: 'pointer', outline: 'none' }}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => [`${value} ball`, 'To\'plangan ball']}
                                            contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    <MotionBox variants={itemVariants} gridColumn={{ base: "1", xl: "1 / span 2" }}>
                        <Card p={6} borderRadius="2xl" boxShadow="xl" bg="white" border="1px solid" borderColor="gray.100" h="100%">
                            <Heading size="sm" mb={6} color="gray.600" textTransform="uppercase" letterSpacing="wider">
                                O'qituvchi Soniga Nisbatan O'rtacha Ballar
                            </Heading>
                            <Box height="450px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data1} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={140} interval={0} fontSize={10} tick={{fill: '#6B7280'}} axisLine={false} tickLine={false} />
                                        <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280'}} fontSize={11} />
                                        <Tooltip 
                                            cursor={{fill: 'rgba(0,0,0,0.05)'}}
                                            contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} name="O'rtacha ball" maxBarSize={50}>
                                            <LabelList dataKey="value" position="top" fill="#4F46E5" fontSize={10} formatter={(val) => val} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    <MotionBox variants={itemVariants} gridColumn={{ base: "1", xl: "1 / span 2" }}>
                        <Card p={6} borderRadius="2xl" boxShadow="xl" bg="white" border="1px solid" borderColor="gray.100">
                            <Heading size="sm" mb={6} color="gray.600" textTransform="uppercase" letterSpacing="wider">
                                Kafedralar Kesimida Jami Ball va O'qituvchilar Soni
                            </Heading>
                            <Box height="450px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data2} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={140} interval={0} fontSize={10} tick={{fill: '#6B7280'}} axisLine={false} tickLine={false} />
                                        <YAxis yAxisId="left" orientation="left" stroke="#4F46E5" axisLine={false} tickLine={false} fontSize={11} />
                                        <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" axisLine={false} tickLine={false} fontSize={11} />
                                        <Tooltip 
                                            cursor={{fill: 'rgba(0,0,0,0.05)'}}
                                            contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend verticalAlign="top" height={36} iconType="circle" />
                                        <Bar yAxisId="left" dataKey="points" name="Jami To'plangan Ball" fill="#4F46E5" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                            <LabelList dataKey="points" position="top" fill="#4F46E5" fontSize={10} />
                                        </Bar>
                                        <Bar yAxisId="right" dataKey="teachers" name="O'qituvchilar Soni" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                            <LabelList dataKey="teachers" position="top" fill="#F59E0B" fontSize={10} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>
                </SimpleGrid>
            </MotionBox>
        </Box>
    );
};

export default ProRectorDashboard;
